import { useState } from "react";
import { CheckCircle2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/States";

const LABEL = { preferences: "Design preferences", proposal: "Design proposal" };
const when = (iso) => new Date(iso).toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function useSignoffs(projectId) { return useFetch(projectId ? `/projects/${projectId}/signoffs` : null); }

export function SignoffStamp({ list = [], kind, className = "" }) {
  const mine = list.filter((s) => s.kind === kind);
  if (!mine.length) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid={`signoff-stamp-${kind}`}>
      {mine.map((s) => <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs text-primary"><CheckCircle2 className="h-3.5 w-3.5" />Approved by {s.name} · {when(s.created_at)}</span>)}
    </div>
  );
}

export function SignoffButton({ projectId, kind, list = [], onChange, dark = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (user.role !== "client") return null;
  const mine = list.find((s) => s.kind === kind && s.user_id === user.id);
  const act = async () => {
    setBusy(true);
    try {
      if (mine) { await api.delete(`/projects/${projectId}/signoffs/${kind}`); toast.message("Approval withdrawn"); }
      else { await api.post(`/projects/${projectId}/signoffs`, { kind }); toast.success(`${LABEL[kind]} approved`); }
      onChange && onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Button onClick={act} disabled={busy} variant={mine ? "outline" : "default"} className={`rounded-full ${dark && mine ? "border-white/40 bg-white/10 text-white hover:bg-white/20" : ""}`} data-testid={`signoff-button-${kind}`}>
      {busy ? <Spinner /> : mine ? <CheckCircle2 className="mr-2 h-4 w-4" /> : <PenLine className="mr-2 h-4 w-4" />}{mine ? "Approved · tap to withdraw" : `Approve ${LABEL[kind].toLowerCase()}`}
    </Button>
  );
}
